import * as React from 'react'
import Card from '@material-ui/core/Card'
import { createMuiTheme, useRedirect } from 'react-admin'
import { useMemo } from 'react'
import { ThemeProvider } from '@material-ui/styles'
import { makeStyles } from '@material-ui/core/styles'
import {
  Avatar,
  Button,
  CardActions,
  CardHeader,
  CircularProgress,
  TextField,
} from '@material-ui/core'
import classnames from 'classnames'
import LockIcon from '@material-ui/icons/Lock'
import { Field, Form } from 'react-final-form'
import { useNotify, useSafeSetState, useTranslate } from 'ra-core'
import Typography from '@material-ui/core/Typography'
import CardContent from '@material-ui/core/CardContent'
import PropTypes from 'prop-types'
import httpClient from '../../providers/httpClient'
import envConfig from '../../environments'

const useStyles = makeStyles(
  theme => ({
    main: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      height: '1px',
      alignItems: 'center',
      justifyContent: 'flex-start',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundImage:
        'radial-gradient(circle at 50% 14em, #313264 0%, #00023b 60%, #00023b 100%)',
    },
    card: {
      minWidth: 300,
      width: 'min-content',
      marginTop: '6em',
    },
    avatar: {
      margin: '1em',
      display: 'flex',
      justifyContent: 'center',
    },
    icon: {
      backgroundColor: theme.palette.secondary[500],
    },
    form: {
      padding: '0 0 1em 0',
    },
    input: {
      marginTop: '1em',
    },
    button: {
      width: '100%',
    },
    formIcon: {
      marginRight: theme.spacing(1),
    },
  }),
  { name: 'RaLogin' },
)

const Input = ({
  meta: { touched, error }, // eslint-disable-line react/prop-types
  input: inputProps, // eslint-disable-line react/prop-types
  ...props
}) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
)

const ForgotPassword = props => {
  const { theme, ...rest } = props
  const muiTheme = useMemo(() => createMuiTheme(theme), [theme])
  const classes = useStyles(props)
  const translate = useTranslate()
  const [loading, setLoading] = useSafeSetState(false)
  const redirect = useRedirect()
  const notify = useNotify()

  const validate = values => {
    const errors = { email: undefined }

    if (!values.email) {
      errors.email = translate('ra.validation.required')
    }
    return errors
  }

  const submit = values => {
    setLoading(true)

    httpClient(`${envConfig.backendUrl}/forgot-password`, {
      method: 'POST',
      body: JSON.stringify(values),
    })
      .then(() => {
        notify('surveys.messages.forgot_password_success', {
          type: 'success',
        })
        redirect('/login')
      })
      .catch(() => {
        notify('surveys.messages.forgot_password_error', {
          type: 'warning',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={classnames(classes.main)} {...rest}>
        <Card className={classes.card}>
          <div className={classes.avatar}>
            <Avatar className={classes.icon}>
              <LockIcon />
            </Avatar>
          </div>
          <CardHeader title={translate('surveys.messages.reset_password')} />
          <CardContent>
            <Typography variant="body2" component="p" color="textSecondary">
              {translate('surveys.messages.reset_password_text')}
            </Typography>
            <Form
              onSubmit={submit}
              validate={validate}
              render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <div className={classes.form}>
                    <div className={classes.input}>
                      <Field
                        autoFocus
                        id="email"
                        name="email"
                        component={Input}
                        label="Email"
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <CardActions>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      disabled={loading}
                      className={classes.button}
                    >
                      {loading && (
                        <CircularProgress
                          className={classes.formIcon}
                          size={18}
                          thickness={2}
                        />
                      )}
                      {translate('surveys.messages.send_instructions')}
                    </Button>
                  </CardActions>
                </form>
              )}
            />
          </CardContent>
        </Card>
      </div>
    </ThemeProvider>
  )
}

ForgotPassword.propTypes = {
  theme: PropTypes.any,
}

export default ForgotPassword
